import React, {FC, useCallback, useState} from 'react';
import {ImageOptimized} from '@/_application/core/components/ui/image';
import {PlayIcon} from '@/_application/icons/play-icon';
import {productPlaceholder} from '@/lib/placeholders';
import {FetchPriority} from '@/_application/core/types';
import Spinner from '../../loaders/spinner/spinner';
interface ImageSource {
    url: string;
    alt: string;
}

interface VideoSlideProps {
    variant: 'video' | 'thumbnail';
    embedUrl?: string;
    imageSrc?: ImageSource;
    width?: number;
    height?: number;
    priority?: boolean;
    fetchPriority?: FetchPriority;
}

export const VideoSlide: FC<VideoSlideProps> = ({
    embedUrl,
    imageSrc,
    width = 400,
    height = 400,
    variant,
    priority = false,
    fetchPriority = FetchPriority.AUTO,
}) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const handlePlayClick = useCallback(() => {
        setIsPlaying(true);
        setIsLoading(true);
    }, []);
    const handleIframeLoad = () => {
        setIsLoading(false);
    };
    if (variant === 'video' && !embedUrl) return <></>;
    if (variant === 'thumbnail' && !imageSrc) return <></>;
    if (variant === 'video' && !isPlaying) {
        return (
            <div>
                <button
                    type="button"
                    aria-label="Video thumbnail"
                    className="w-full items-center justify-center cursor-pointer"
                    onClick={handlePlayClick}>
                    <ImageOptimized
                        src={imageSrc?.url ?? productPlaceholder}
                        alt={imageSrc?.alt ?? 'video-thumbnail'}
                        width={width}
                        height={height}
                        quality={75}
                        className="object-cover"
                        priority={priority}
                        fetchPriority={fetchPriority}
                        sizes="424px"
                    />
                    <div
                        className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform
                        items-center justify-center rounded-full border border-white bg-slate-500">
                        <PlayIcon className="h-4 w-4 text-white" />
                    </div>
                </button>
            </div>
        );
    }

    if (variant === 'video' && isPlaying) {
        return (
            <div className="product-video-iframe m-auto flex! h-full w-full items-center justify-center">
                {isLoading && (
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                        <Spinner className="h-8 w-8" simple={true} />
                    </div>
                )}
                <iframe
                    width={width}
                    height={height}
                    src={`${embedUrl}?autoplay=1`}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    title="YouTube Video"
                    onLoad={handleIframeLoad}
                />
            </div>
        );
    }
    if (variant !== 'thumbnail') return null;
    return (
        <>
            <div className="flex w-full items-center justify-center">
                <ImageOptimized
                    src={imageSrc?.url ?? productPlaceholder}
                    alt={imageSrc?.alt ?? 'thumbnail-slide'}
                    width={70}
                    height={70}
                    quality={75}
                    draggable={false}
                    className="w-16 h-16 object-cover select-none"
                    sizes="70px"
                />
            </div>
            <div
                className="absolute left-1/2 top-1/2 flex h-6 w-6 -translate-x-1/2 -translate-y-1/2 transform
                    items-center justify-center rounded-full border border-white bg-slate-500">
                <PlayIcon className="h-2 w-2 text-white" />
            </div>
        </>
    );
};
